import React, { useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  FooterAction,
} from '@ohif/ui-next';

interface UploadConfirmDialogProps {
  open: boolean;
  errorMessage: string;
  /** False for a transport or server fault, where forcing answers nothing. */
  canForce: boolean;
  isResolving: boolean;
  onContinue: () => void;
  onStop: () => void;
}

/**
 * §5.4. NON-DISMISSIBLE, and that is the fix for §6 defect 5: develop's Esc and X
 * both ran `onClose` (DicomUpload.tsx:274), which neither continued nor stopped —
 * so the upload sat halted with no exit at all and no way back to the drop state.
 * Two exits, both explicit.
 *
 * `unstyled` is what suppresses the built-in close X: DialogContent renders
 * <DialogPrimitive.Close> unconditionally otherwise
 * (platform/ui-next/src/components/Dialog/Dialog.tsx:116). It costs only the
 * `w-full` class, which is added back below — every other base class still applies.
 *
 * The dismissal props sit on the Dialog ROOT, not on DialogContent: 3.13 moved them
 * there and DialogContent reads them from context (:76-77). Same shape as
 * OrganReviewDialog.tsx:118-135.
 */
export default function UploadConfirmDialog({
  open,
  errorMessage,
  canForce,
  isResolving,
  onContinue,
  onStop,
}: UploadConfirmDialogProps) {
  // Guards the click rather than passing `disabled`, for the reason
  // OrganReviewDialog.tsx:108-110 gives: a native <button disabled> leaves the tab
  // order, and FooterAction's ActionProps.onClick is required (FooterAction.tsx:11)
  // so the handler cannot simply be dropped.
  const continueIfEnabled = useCallback(() => {
    if (!isResolving) {
      onContinue();
    }
  }, [isResolving, onContinue]);

  const stopIfEnabled = useCallback(() => {
    if (!isResolving) {
      onStop();
    }
  }, [isResolving, onStop]);

  return (
    <Dialog
      open={open}
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
    >
      <DialogContent
        unstyled
        className="w-full sm:max-w-md"
        data-cy="sycai-upload-confirm-dialog"
      >
        <DialogHeader>
          <DialogTitle>Upload Error</DialogTitle>
          {/*
            Radix warns when DialogContent has neither a description nor
            aria-describedby={undefined} (@radix-ui/react-dialog/dist/index.mjs:302-311).
            This one is visible rather than sr-only, because it is develop's copy.
          */}
          {/*
            DialogDescription inherits the same no-ancestor-`color` gap as the <p>s
            below -- measured 1.552:1 unstyled. text-white matches its siblings
            rather than a new colour.
          */}
          <DialogDescription className="py-2 text-white">
            An error occurred during file upload:
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/*
            M5.4 D13: this <p> was not unstyled -- it carried text-yellow-400, a severity
            colour with no develop equivalent. develop's own text-warning (DicomUpload.tsx:230)
            was never a colour key in its Tailwind config and compiled to nothing, so the
            yellow this fork invented was never real parity. Replaced with text-white,
            matching the sibling below, rather than restored.
          */}
          <p className="text-white">{errorMessage}</p>
          <p className="text-white">Do you want to continue uploading?</p>
        </div>

        <FooterAction className="gap-4 py-2">
          {/*
            M5.5 D5. Same dismiss idiom as DeleteConfirmDialog.tsx's Cancel -- Stop exits the
            flow rather than advancing it, and Secondary resolves to a 40% orange wash.
            develop's own equivalent is Button type="secondary" hover:text-white/80
            (DicomUploadConfirmationModal.tsx), which is a label dim over no fill at all; the
            two hover classes here are what drop ghost's hover:bg-primary/25 through twMerge.
          */}
          <FooterAction.Auxiliary
            className="hover:text-foreground/60 flex-1 hover:bg-transparent aria-disabled:opacity-50"
            dataCY="sycai-upload-stop"
            onClick={stopIfEnabled}
            aria-disabled={isResolving || undefined}
          >
            Stop
          </FooterAction.Auxiliary>
          {/*
            Whole-milestone review I1, user ruling: Stop ALONE when the halt was not
            a content rejection. Continue re-sends every remaining file with
            x-skip-validation, so offering it for a 502 or a dead connection invites
            the user to disable validation for the rest of the study to fix a
            network fault. The copy is untouched — M5 owns this feature's copy pass
            (parent §5.3), and only the button is conditional.
          */}
          {/* M5.5 R-K: white on the orange accent, as a class -- see DropZone.tsx for why
              not --primary-foreground. */}
          {canForce && (
            <FooterAction.Primary
              className="flex-1 text-white aria-disabled:opacity-50"
              dataCY="sycai-upload-continue"
              onClick={continueIfEnabled}
              aria-disabled={isResolving || undefined}
            >
              Continue
            </FooterAction.Primary>
          )}
        </FooterAction>
      </DialogContent>
    </Dialog>
  );
}
